import React from "react";
// import TextField from "@material-ui/core/TextField";
import { states, countries } from "temp/index";
import _ from "lodash";
import { Autocomplete, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, Paper, TextField, Tooltip } from "@mui/material";
// import FormControl from "@material-ui/core/FormControl";
import { UserContext } from "context/User";
import SuccessAni from "components/utils/SuccessAni";
import { AddressContext } from "context/Address";
import { makeAxiosCall } from "utils/";
import { AddressInterface, AddressTypeInterface, createEmptyAddress, DEFAULT_ADDITIONAL_SHIPPING_OPTIONS } from "interfaces/Address";
import ContactSelect from "../Contact/ContactSelect";
import { ContactInterface } from "interfaces/Contact";
import ContactEditForm from "components/MyCompany/ContactEditForm";
import { buildCompanyInfo, CompanyInterface } from "interfaces/Company";
import { useNotification } from "context/Notification";
// import { Alert, Autocomplete } from "@material-ui/lab";

interface AddressFormValidation {
    streetAddress: string,
    city: string,
    state: string,
    zip: string,
    country: string,
    addressType: string,
    contact: string,
}

interface AddressFormProps {
    companyId: number,
    address?: AddressInterface,
    addCallback?: (address: AddressInterface) => void,
    updateCallback?: (address: AddressInterface, id: number) => void,
    deleteCallback?: (addressId: number) => void,
    addressType?: string,
    skipAdvancedOptions?: boolean
}

const AddressForm = ({ companyId, address, addCallback, updateCallback, deleteCallback, addressType, skipAdvancedOptions }: AddressFormProps) => {
    const { currentUser, fetchCurrentUser } = React.useContext(UserContext);
    const { addNewAddress, updateAddress, deleteAddress, addressTypes } = React.useContext(AddressContext);
    const { showNotification } = useNotification();

    const startingValidation: AddressFormValidation = {
        streetAddress: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        addressType: "",
        contact: "",
    };

    const INITIAL_NEW_ADDRESS: AddressInterface = createEmptyAddress();

    const [form, setForm] = React.useState<AddressInterface>(INITIAL_NEW_ADDRESS);
    const [company, setCompany] = React.useState<CompanyInterface>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [failureAlert, setFailureAlert] = React.useState(false);
    const [showShippingContactForm, setShowShippingContactForm] = React.useState(false);
    const [showAdvancedShippingOptions, setShowAdvancedShippingOptions] = React.useState(false);
    const [currentProcess, setCurrentProcess] = React.useState<string>("");
    const [validation, setValidation] = React.useState(startingValidation);
    const inputLabel = React.useRef(null);
    const formControl = "16px";

    React.useEffect(() => {
        if (!companyId || companyId == currentUser.company.id) {
            setCompany(currentUser.company);
        } else {
            fetchCompany(companyId);
        }
    }, []);

    React.useEffect(() => {
        if (address) {
            if (address.addressTypes.findIndex((t) => ["Shipping", "Primary", "Drop Ship"].includes(t.name)) > -1) {
                if(!address.additionalShippingDetails) {
                    address.additionalShippingDetails = { ...DEFAULT_ADDITIONAL_SHIPPING_OPTIONS };
                }

                // Don't auto-assign a receiving contact. When creating an address
                // for another company (e.g. a rep building a reseller's address) the
                // creator's own contact would get saved onto that company's address —
                // a foreign-company contact. A distinct receiving contact is rare; if
                // they want one, they pick it explicitly.

                setForm(address);
            } else {
                setForm(address);
            }
        } else {
            setForm(INITIAL_NEW_ADDRESS);
        }
    }, [address]);


    const fetchCompany = async (companyId: number) => {
        const response = await makeAxiosCall(
            "get",
            `company/${companyId}`
        ).catch(error => {
            console.error("Caught error getting company info:");
            console.error(error);
        });

        if (response.data) {
            const companyData: CompanyInterface = await buildCompanyInfo(response.data);
            setCompany(companyData);
        }
    };


    const handleSave = async () => {
        if ((!form.additionalShippingDetails || !form.additionalShippingDetails.id) && address) {
            if (address.additionalShippingDetails && address.additionalShippingDetails.id) {
                // TODO: DELETE EXISTING ADDITIONAL SHIPPING OPTIONS
            }
            address.additionalShippingDetails = null;
        }

        const response = address ? await updateAddress(form, company.id) : await addNewAddress(form, company.id, addressType);

        setCurrentProcess("saving");
        if (response && response.data.id) {
            const newAddressObject: AddressInterface = {
                contactName: form.contactName,
                streetAddress: form.streetAddress,
                streetAddressTwo: form.streetAddressTwo,
                city: form.city,
                state: form.state,
                zip: form.zip,
                country: form.country,
                addressTypes: form.addressTypes,
                id: response.data.id,
                additionalShippingDetails: form.additionalShippingDetails
            };

            if (!address) {
                if (addCallback) {
                    addCallback(newAddressObject);
                }
            } else {
                if (updateCallback) updateCallback(newAddressObject, response.data.id);
            }
        } else {
            console.log("failure?");

            setFailureAlert(true);
        }
        setCurrentProcess("");
    };

    const handleDelete = async () => {
        let hasPrimary = false;
        let hasBilling = false;
        let hasShipping = false;

        for (let ai = 0; ai < company.addresses.length; ai++) {
            const existingAddress = company.addresses[ai];
            if(existingAddress.id != address.id) {
                for (let ati = 0; ati < existingAddress.addressTypes.length; ati++) {
                    const addressType = existingAddress.addressTypes[ati];
                    if(addressType.id == 1) {
                        hasPrimary = true;
                    } else if(addressType.id == 2) {
                        hasBilling = true;
                    } else if(addressType.id == 3) {
                        hasShipping = true;
                    }
                }
            }
        }
        if(!hasPrimary && address.addressTypes.find((at) => at.id == 1)) {
            showNotification("You must have at least one primary address.", "error", { duration: 5000 });
            return;
        }

        if(!hasBilling && address.addressTypes.find((at) => at.id == 2)) {
            showNotification("You must have at least one billing address.", "error", { duration: 5000 });
            return;
        }

        if(!hasShipping && address.addressTypes.find((at) => at.id == 3)) {
            showNotification("You must have at least one shipping address.", "error", { duration: 5000 });
            return;
        }

        setCurrentProcess("delete");
        const response = await deleteAddress(address.id);
        if (response && response.data.id) {
            if (deleteCallback) deleteCallback(response.data.id);
        } else {
            setFailureAlert(true);
            setCurrentProcess("");
        }
    };

    const handleAddressChange = (name: string) => (e) => {

        setForm({
            ...form,
            [name]: e.target.value,
        });
        // if (name === "state") {
        //   let state = findState(e.target.value);
        //   setForm({
        //     ...form,
        //     state: state.code
        //   });
        // } else if (name === "country") {


        //   let country = findCountry(e.target.value);
        //   setForm({
        //     ...form,
        //     country: country.name,
        //     countryId: e.target.value
        //   });
        // } else {
        //   setForm({
        //     ...form,
        //     [name]: e.target.value,
        //   });
        // }
        setValidation({ ...validation, [name]: "" });
    };

    const handleType = (type: AddressTypeInterface) => {
        let newTypes = [];
        let newAdditionalDetails = null;
        if (form.addressTypes.some((selected) => selected.id === type.id)) {
            newTypes = _.filter(form.addressTypes, (selected) => selected.id !== type.id);
        } else {
            newTypes = [...form.addressTypes, type];
        }

        // Make sure that these types of addresses have additional details.
        if(newTypes.findIndex((t) => ["Shipping", "Primary", "Drop Ship"].includes(t.name)) > -1) {
            if(!form.additionalShippingDetails) {
                // Receiving contact intentionally left unset (null) — see the [address]
                // effect above. Spread so we don't mutate the shared default const.
                newAdditionalDetails = { ...DEFAULT_ADDITIONAL_SHIPPING_OPTIONS };
            } else {
                newAdditionalDetails = form.additionalShippingDetails;
            }
        } else {
            newAdditionalDetails = null;
        }

        setForm({ ...form, addressTypes: newTypes, additionalShippingDetails: newAdditionalDetails });
    };

    const validate = () => {
        const errors: AddressFormValidation = {
            streetAddress: "",
            city: "",
            state: "",
            zip: "",
            country: "",
            addressType: "",
            contact: "",
        };

        errors.streetAddress = form.streetAddress.length === 0 ? "Required" : "";
        errors.city = form.city.length === 0 ? "Required" : "";
        errors.state = form.state.length === 0 ? "Required" : "";
        errors.zip = form.zip.length === 0 ? "Required" : "";
        errors.country = form.country.length === 0 ? "Required" : "";
        errors.addressType = form.addressTypes.length === 0 ? "Required" : "";

        if (showAdvancedShippingOptions) {
            errors.contact = form.additionalShippingDetails.contact ? "" : "Required";
        }

        if(address && currentUser && currentUser.company && currentUser.company.addresses) {
            // if this is an update, make sure they're not removing types that are required... somehow
            if(address.addressTypes != form.addressTypes) {
                // changing types
                let hasPrimary = false;
                let hasBilling = false;
                let hasShipping = false;
                for (let ai = 0; ai < currentUser.company.addresses.length; ai++) {
                    const existingAddress = currentUser.company.addresses[ai];
                    if(existingAddress.id != address.id) {
                        for (let ati = 0; ati < existingAddress.addressTypes.length; ati++) {
                            const addressType = existingAddress.addressTypes[ati];
                            if(addressType.id == 1) {
                                hasPrimary = true;
                            } else if(addressType.id == 2) {
                                hasBilling = true;
                            } else if(addressType.id == 3) {
                                hasShipping = true;
                            }
                        }
                    }
                }
                if(!hasPrimary && address.addressTypes.find((at) => at.id == 1) && !form.addressTypes.find((at) => at.id == 1)) {
                    errors.addressType += "You must have at least one primary address. "
                }

                if(!hasBilling && address.addressTypes.find((at) => at.id == 2) && !form.addressTypes.find((at) => at.id == 2)) {
                    errors.addressType += "You must have at least one billing address. "
                }

                if(!hasShipping && address.addressTypes.find((at) => at.id == 3) && !form.addressTypes.find((at) => at.id == 3)) {
                    errors.addressType += "You must have at least one shipping address. "
                }
            }
        }

        setValidation({ ...errors });

        return Object.values(errors).every(x => x === "");
    };

    return (
        <>
            <Grid size={{ xs: 12 }}>
                <TextField
                    style={{ width: "100%" }}
                    label="Addressee"
                    margin="normal"
                    value={form.contactName}
                    variant="outlined"
                    onChange={handleAddressChange("contactName")}
                    inputProps={{ maxLength: 40 }}
                    placeholder={currentUser.displayName}
                    autoComplete="name"
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <TextField
                    style={{ width: "100%" }}
                    label="Street Address"
                    margin="normal"
                    className="street"
                    variant="outlined"
                    onChange={handleAddressChange("streetAddress")}
                    value={form.streetAddress}
                    error={validation.streetAddress.length !== 0}
                    helperText={validation.streetAddress}
                    inputProps={{ maxLength: 90 }}
                    autoComplete="street-address"
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <TextField
                    style={{ width: "100%" }}
                    label="Address Line 2"
                    margin="normal"
                    className="street"
                    variant="outlined"
                    onChange={handleAddressChange("streetAddressTwo")}
                    value={form.streetAddressTwo}
                    inputProps={{ maxLength: 90 }}
                    autoComplete="address-line2"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                    style={{ width: "100%" }}
                    label="City"
                    margin="normal"
                    className="city"
                    variant="outlined"
                    onChange={handleAddressChange("city")}
                    value={form.city}
                    error={validation.city.length !== 0}
                    helperText={validation.city}
                    inputProps={{ maxLength: 30 }}
                    autoComplete="address-level2"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl
                    style={{ marginTop: formControl, width: "100%" }}
                    className="state"
                    variant="outlined"
                    error={validation.state.length !== 0}
                >
                    <Autocomplete
                        id="state-select-outlined"
                        // Options are the full state objects (deduped by code — WA is both
                        // Washington and Western Australia) so we can match on BOTH the name
                        // and the code. That's what lets browser autofill (which injects the
                        // full name, e.g. "Wisconsin") resolve to the right option.
                        options={_.uniqBy(states, "code")}
                        // Guard on a non-empty form.state: some entries have an empty code, so
                        // an empty form.state would otherwise match one of them (e.g. "Guayas").
                        value={form.state ? (states.find((s) => s.name === form.state || s.code === form.state) ?? null) : null}
                        isOptionEqualToValue={(o, v) => o.code === v.code}
                        getOptionLabel={(o) => (o && o.name) ? o.name : ""}
                        // Match the typed/autofilled text against name OR code.
                        filterOptions={(opts, params) => {
                            const query = params.inputValue.trim().toLowerCase();
                            if (!query) return opts;
                            return opts.filter((o) => o.name.toLowerCase().includes(query) || o.code.toLowerCase().startsWith(query));
                        }}
                        onChange={(e, opt) => setForm({ ...form, state: opt && opt.name ? opt.name : "" })}
                        // Reconcile on input (typing OR autofill): an exact name/code match
                        // selects the option immediately. Guarded to reason==="input" and to
                        // a real change, so the value->input reset can't create a loop.
                        onInputChange={(e, val, reason) => {
                            if (reason !== "input") return;
                            const query = (val || "").trim().toLowerCase();
                            const match = states.find((s) => s.name.toLowerCase() === query || s.code.toLowerCase() === query);
                            if (match && match.name !== form.state) setForm({ ...form, state: match.name });
                        }}
                        autoHighlight
                        autoSelect
                        renderInput={(p) => <TextField
                            {...p}
                            inputProps={{
                                ...p.inputProps,
                                autoComplete: "address-level1",
                            }}
                            label="State" variant="outlined" name="state" />}
                    />
                    <FormHelperText>{validation.state}</FormHelperText>
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                    style={{ width: "100%" }}
                    label="Postal Code"
                    margin="normal"
                    className="postal"
                    variant="outlined"
                    onChange={handleAddressChange("zip")}
                    value={form.zip}
                    error={validation.zip.length !== 0}
                    helperText={validation.zip}
                    inputProps={{ maxLength: 10 }}
                    autoComplete="postal-code"
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <FormControl
                    style={{ marginTop: formControl, width: "100%" }}
                    variant="outlined"
                >
                    <Autocomplete
                        id="country-select-outlined"
                        // Full objects so ISO codes can match in filterOptions below. We
                        // deliberately do NOT rewrite the input or auto-select on a code
                        // match while typing: 131 country names pass through another
                        // country's 2-letter code ("Ne(w Zealand)" = Niger), so any
                        // mid-typing snap traps the user on the wrong country. Unlike the
                        // State field there's no autofill to reconcile here — it's
                        // suppressed on this input.
                        options={countries}
                        // Case-insensitive so legacy stored values ("United States", "US")
                        // still resolve. Unmatched legacy values ("USA", "") just display
                        // empty — form.country only changes on an explicit selection, so
                        // saving another field won't clobber them.
                        value={form.country ? (countries.find((c) => c.name.toLowerCase() === form.country.toLowerCase() || c.abbreviation.toLowerCase() === form.country.toLowerCase()) ?? null) : null}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(o) => (o && o.name) ? o.name : ""}
                        // Match the typed text against name OR ISO code — suggestions only,
                        // the input text is never replaced out from under the user.
                        filterOptions={(opts, params) => {
                            const query = params.inputValue.trim().toLowerCase();
                            if (!query) return opts;
                            return opts.filter((o) => o.name.toLowerCase().includes(query) || o.abbreviation.toLowerCase().startsWith(query));
                        }}
                        // Store the full name: portal consumers and Fishbowl SO creation
                        // validate country against full names (countryconst.name), not codes.
                        onChange={(e, opt) => setForm({ ...form, country: opt && opt.name ? opt.name : "" })}
                        autoHighlight
                        autoSelect
                        renderInput={(p) => <TextField
                            {...p}
                            // Suppress browser autofill (see the State field) — it corrupts the combobox.
                            inputProps={{
                                ...p.inputProps,
                                autoComplete: "new-password",
                            }}
                            label="Country" variant="outlined" name="country-select" />}
                    />
                    <FormHelperText>{validation.country}</FormHelperText>
                </FormControl>
            </Grid>

            {form.additionalShippingDetails ?
                <Grid container size={{ xs: 12 }} style={{textAlign: "left"}}>
                    <Paper className={"bordered-container"} style={{padding: "0 15px"}}>
                        <Grid container size={{ xs: 12 }}>
                            <Grid container size={{ md: 6, xs: 12 }}>
                                <Grid size={{ xs: 12 }}>
                                    <h3 className="center">Location Details</h3>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Tooltip title={"Does this address have a truck height loading dock?"} placement="top" arrow>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.additionalShippingDetails.hasTruckHeightLoadingDock}
                                                    onChange={() => {
                                                        setForm({
                                                            ...form,
                                                            additionalShippingDetails: {
                                                                ...form.additionalShippingDetails,
                                                                hasTruckHeightLoadingDock: !form.additionalShippingDetails.hasTruckHeightLoadingDock,
                                                                liftGateRequired: form.additionalShippingDetails.hasTruckHeightLoadingDock
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={"Truck Height Loading Dock"}
                                            classes={{ label: "search-filter-option" }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Tooltip title={"Is this a residential location?"} placement="top" arrow>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!form.additionalShippingDetails.isCommercialLocation}
                                                    onChange={() => {
                                                        setForm({
                                                            ...form,
                                                            additionalShippingDetails: {
                                                                ...form.additionalShippingDetails,
                                                                isCommercialLocation: !form.additionalShippingDetails.isCommercialLocation
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={"Residential address"}
                                            classes={{ label: "search-filter-option" }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Tooltip title={"Is this a Job Site?"} placement="top" arrow>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.additionalShippingDetails.isJobsiteLocation}
                                                    onChange={() => {
                                                        setForm({
                                                            ...form,
                                                            additionalShippingDetails: {
                                                                ...form.additionalShippingDetails,
                                                                isJobsiteLocation: !form.additionalShippingDetails.isJobsiteLocation
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={"Job site"}
                                            classes={{ label: "search-filter-option" }}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid container size={{ xs: 12, md: 6 }}>
                                <Grid size={{ xs: 12 }}>
                                    <h3 className="center">Available Delivery Hours</h3>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Tooltip title={"Does this address allow for deliveries between 8AM and Noon?"} placement="top" arrow>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.additionalShippingDetails.deliveryHoursMorning}
                                                    onChange={() => {
                                                        setForm({
                                                            ...form,
                                                            additionalShippingDetails: {
                                                                ...form.additionalShippingDetails,
                                                                deliveryHoursMorning: !form.additionalShippingDetails.deliveryHoursMorning
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={"8AM - NOON"}
                                            classes={{ label: "search-filter-option" }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Tooltip title={"Does this address allow for afternoon deliveries?"} placement="top" arrow>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.additionalShippingDetails.deliveryHoursAfternoon}
                                                    onChange={() => {
                                                        setForm({
                                                            ...form,
                                                            additionalShippingDetails: {
                                                                ...form.additionalShippingDetails,
                                                                deliveryHoursAfternoon: !form.additionalShippingDetails.deliveryHoursAfternoon
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={"NOON - 4PM"}
                                            classes={{ label: "search-filter-option" }}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            <Grid container size={{ xs: 12 }}>
                                
                                    <Grid size={{ xs: 12 }}>
                                        <Tooltip title={"Does this location have limited or special access?"} placement="top" arrow>
                                            <TextField
                                                style={{ width: "100%" }}
                                                label={"Location Notes"}
                                                margin="normal"
                                                value={form.additionalShippingDetails.locationNotes}
                                                variant="outlined"
                                                onChange={(e) => {
                                                    setForm({
                                                        ...form,
                                                        additionalShippingDetails: {
                                                            ...form.additionalShippingDetails,
                                                            locationNotes: e.target.value
                                                        }
                                                    });
                                                }}
                                                placeholder={"Does this location have limited or special access?"}
                                            />
                                        </Tooltip>
                                    </Grid>
                            </Grid>

                            <Grid container size={{ xs: 12 }}>
                                    <Grid size={{ xs: 12 }}>
                                        <ContactSelect
                                            company={company}
                                            label="Recieving Contact"
                                            setNewFormActive={(open) => {
                                                setShowShippingContactForm(open);
                                            }}
                                            currentSelection={form.additionalShippingDetails.contact}
                                            onSelect={(contact: ContactInterface) => {

                                                setForm({
                                                    ...form,
                                                    additionalShippingDetails: {
                                                        ...form.additionalShippingDetails,
                                                        contact: contact
                                                    }
                                                });
                                            }}
                                        />

                                        {validation.contact.length > 0 && (
                                            <p style={{ color: "red" }}>
                                                Required
                                            </p>
                                        )}

                                        {showShippingContactForm ? <ContactEditForm
                                            createCallback={(contact: ContactInterface, newId: number) => {
                                                contact.id = newId;
                                                (company).contacts.push(contact);
                                                setShowShippingContactForm(false);
                                                setForm({
                                                    ...form,
                                                    additionalShippingDetails: {
                                                        ...form.additionalShippingDetails,
                                                        contact: contact
                                                    }
                                                });
                                                // Refresh the user so the new receiving contact shows up in the
                                                // shipping-contact list (the in-place push above mutates the array
                                                // without a new reference, so consumers don't re-render on their own).
                                                fetchCurrentUser();
                                            }}
                                            companyId={company.id}
                                        /> : null}
                                    </Grid>
                            </Grid>
                        </Grid>
                    </Paper>

                </Grid>
                : null
            }

            <Grid size={{ xs: 12 }} style={{marginTop: 12, minHeight: 100}}>
                <FormControl fullWidth error={validation.addressType.length !== 0}>
                    <FormLabel ref={inputLabel}>Address Type: {validation.addressType}</FormLabel>
                    <FormGroup row className="contact-type-flex" style={{alignSelf: "center"}}>
                        {
                            addressTypes.map((type) => {
                                return <FormControlLabel
                                    key={type.id}
                                    control={
                                        <Tooltip title={type.name == "Billing" && !currentUser.accountLevel.canEditPaymentMethods ? "Only Admins can edit billing addresses" : ""}>
                                            <div>
                                                <Checkbox
                                                    checked={form.addressTypes.some((selected) => selected.id == type.id)}
                                                    onChange={() => handleType(type)}
                                                    disabled={type.name == "Billing" && !currentUser.accountLevel.canEditPaymentMethods}
                                                />
                                            </div>
                                        </Tooltip>
                                    }
                                    label={type.name}
                                />;
                            })
                        }
                    </FormGroup>
                </FormControl>
            </Grid>
            <Grid container justifyContent="center" alignItems="center" spacing={2} size={{ xs: 12 }}>
                {address && address.id && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <Button
                            type="submit"
                            style={{ width: "100%" }}
                            variant="contained"
                            color="primary"
                            onClick={handleDelete}
                            className="btn red"
                        >
                            {currentProcess === "delete" ?
                                <CircularProgress
                                    color="inherit"
                                    size={24}
                                /> :
                                "Delete"
                            }
                        </Button>
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Button
                        type="submit"
                        style={{ width: "100%" }}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSuccessAlert(false);
                            setFailureAlert(false);
                            if (validate()) {
                                setCurrentProcess("saving");
                                handleSave();
                            }
                        }}
                        className="btn"
                    >
                        {currentProcess === "saving" ?
                            <CircularProgress
                                color="inherit"
                                size={24}
                            /> :
                            "Save Address"
                        }
                    </Button>
                </Grid>
            </Grid>
            {successAlert &&
                <Grid container justifyContent="center">
                    <SuccessAni marginTop={20} title="Saved Address"></SuccessAni>
                </Grid>
            }
            {failureAlert &&
                <Grid container justifyContent="center" direction="column" style={{ color: "#d06079", textAlign: "center", marginTop: 20 }}>
                    <i className="fa-light fa-triangle-exclamation" style={{ fontSize: "3em" }}></i>
                    <p style={{ fontSize: "1.25em" }}>Failed to Save Address</p>
                    <p>Please try again. If the issue persists, contact our support.</p>
                </Grid>
            }

        </>
    );
};

export default AddressForm;
